import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/api';

function APIKeyManager() {
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api-keys/`);
      setApiKeys(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const createAPIKey = async () => {
    if (!newKeyName.trim()) {
      alert('Please enter a name for the API key');
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(`${API_BASE}/api-keys/`, {
        name: newKeyName
      });
      setNewlyCreatedKey(response.data);
      setNewKeyName('');
      loadAPIKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteAPIKey = async (id) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api-keys/${id}/`);
      loadAPIKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key');
    }
  };

  const toggleActive = async (id) => {
    try {
      await axios.post(`${API_BASE}/api-keys/${id}/toggle_active/`);
      loadAPIKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
      alert('Failed to toggle API key status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="api-key-manager">
      <div className="api-key-header">
        <h2>API Keys</h2>
      </div>

      {newlyCreatedKey && (
        <div className="new-key-alert">
          <h3><i className="fas fa-exclamation-triangle"></i> Save Your API Key</h3>
          <p>This is the only time you'll see this key. Copy it now!</p>
          <div className="key-display">
            <code>{newlyCreatedKey.key}</code>
            <button 
              className="btn btn-sm"
              onClick={() => copyToClipboard(newlyCreatedKey.key)}
            >
              Copy
            </button>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setNewlyCreatedKey(null)}
          >
            I've saved it
          </button>
        </div>
      )}

      <div className="create-key-section">
        <h3>Create New API Key</h3>
        <div className="input-group">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="API Key Name (e.g., 'Automation Script')"
            disabled={isCreating}
          />
          <button
            onClick={createAPIKey}
            disabled={isCreating || !newKeyName.trim()}
            className="btn btn-primary"
          >
            {isCreating ? 'Creating...' : 'Create Key'}
          </button>
        </div>
      </div>

      <div className="api-keys-list">
        <h3>Your API Keys</h3>
        {apiKeys.length === 0 ? (
          <p className="empty-state">No API keys yet. Create one to get started!</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Key Prefix</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map(key => (
                <tr key={key.id}>
                  <td>{key.name}</td>
                  <td><code>{key.key_prefix}...</code></td>
                  <td>{new Date(key.created_at).toLocaleDateString()}</td>
                  <td>{key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <span className={`status-badge ${key.is_active ? 'active' : 'inactive'}`}>
                      {key.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleActive(key.id)}
                      className="btn btn-sm"
                    >
                      {key.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => deleteAPIKey(key.id)}
                      className="btn btn-sm btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="api-docs-section">
        <button 
          className="btn btn-secondary"
          onClick={() => setShowDocs(!showDocs)}
        >
          {showDocs ? 'Hide' : 'Show'} API Documentation
        </button>

        {showDocs && (
          <div className="api-docs">
            <h3>API Documentation</h3>
            <p>
              Full docs:{" "}
              <a href="https://ai.metting.net" target="_blank" rel="noreferrer">
                https://ai.metting.net
              </a>
            </p>
            
            <h4>Authentication</h4>
            <p>Include your API key in the Authorization header:</p>
            <pre><code>Authorization: Bearer sk-your-api-key-here</code></pre>

            <h4>Endpoints</h4>
            <p>You can call either the legacy automation endpoints below or the new OpenAI-compatible endpoints using any OpenAI SDK. For OpenAI SDKs, set the base URL to <code>http://localhost:8000</code> (or your deployed domain) and keep your existing integration code.</p>

            <h5>OpenAI-Compatible Endpoints</h5>
            <p>Use standard OpenAI clients by overriding the base URL:</p>
            <pre><code>{`# Example (Python)
from openai import OpenAI

client = OpenAI(
    api_key="sk-your-api-key",
    base_url="http://localhost:8000"
)

resp = client.chat.completions.create(
    model="llama2",
    messages=[{"role": "user", "content": "Hello!"}]
)`}</code></pre>

            <div className="endpoint">
              <h5>GET /v1/models</h5>
              <p>List locally available Ollama models (OpenAI-compatible response)</p>
              <pre><code>{`curl http://localhost:8000/v1/models \\
  -H "Authorization: Bearer sk-your-api-key"`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /v1/chat/completions</h5>
              <p>OpenAI chat completions endpoint mapped to Ollama chat</p>
              <pre><code>{`{
  "model": "llama2",
  "messages": [
    {"role": "system", "content": "You are helpful."},
    {"role": "user", "content": "Write a haiku about autumn"}
  ],
  "stream": false
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /v1/completions</h5>
              <p>Legacy text completions endpoint (single prompt)</p>
              <pre><code>{`{
  "model": "llama2",
  "prompt": "The quick brown fox",
  "max_tokens": 100
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /v1/responses</h5>
              <p>OpenAI Responses endpoint (maps to chat completions)</p>
              <pre><code>{`{
  "model": "llama2",
  "input": "Summarize our meeting notes."
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /v1/embeddings</h5>
              <p>Placeholder endpoint. Returns 501 (not implemented) so you can detect unsupported features gracefully.</p>
            </div>

            <div className="endpoint">
              <h5>POST /v1/images/generations</h5>
              <p>OpenAI image generation-compatible endpoint (requires local generator setup)</p>
              <pre><code>{`{
  "prompt": "A watercolor hummingbird",
  "model": "stable-diffusion",
  "size": "512x512"
}`}</code></pre>
            </div>

            <h5>Legacy Automation Endpoints</h5>
            <div className="endpoint">
              <h5>POST /api/v1/complete/</h5>
              <p>Generate text completion</p>
              <pre><code>{`{
  "prompt": "Your prompt here",
  "model": "llama2",  // optional
  "max_tokens": 500,  // optional
  "temperature": 0.7  // optional
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /api/v1/summarize/</h5>
              <p>Summarize text</p>
              <pre><code>{`{
  "text": "Long text to summarize...",
  "model": "llama2",  // optional
  "length": "medium"  // short, medium, or long
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /api/v1/analyze/</h5>
              <p>Analyze text</p>
              <pre><code>{`{
  "text": "Text to analyze...",
  "task": "sentiment",  // sentiment, keywords, topics, general
  "model": "llama2"     // optional
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /api/v1/tool/</h5>
              <p>Invoke a custom tool by short name</p>
              <pre><code>{`{
  "tool": "summarize",      // short_name of your tool
  "data": "Text to process...",
  "model": "llama2"         // optional
}`}</code></pre>
              <p><strong>Note:</strong> Tools are user-defined templates in the Tool Library. Create a tool with a short_name (e.g., "summarize", "translate") and instructions. The API will combine your tool's instructions with the provided data.</p>
            </div>

            <div className="endpoint">
              <h5>POST /api/waterfalls/execute/&lt;short_name&gt;/</h5>
              <p>Execute a waterfall pipeline by its short name.</p>
              <pre><code>{`{
  "input_data": {
    "text": "Process this content",
    "url": "https://example.com"
    // any fields your first step needs
  }
}`}</code></pre>
              <p><strong>Note:</strong> Waterfalls are ordered chains of tools. Create one in the Waterfalls tab and give it a <code>short_name</code>. The <code>input_data</code> fields become available as <code>{'{{input.field}}'}</code> in step mappings.</p>

              <p><strong>Variable mapping:</strong> Each step's <code>input_mapping</code> is a key→value object where values can contain <code>{'{{source.field}}'}</code> templates:</p>
              <pre><code>{`// Template syntax in step input_mapping:
{
  "prompt": "Summarize this: {{input.text}}",
  "context": "{{step_0.result}}",      // output from step 1
  "extra":   "{{step_1.summary}}"      // output from step 2
}
// Sources: "input" = original input_data
//          "step_0" = first step's output, "step_1" = second, etc.`}</code></pre>

              <p><strong>Conditional branching:</strong> Steps with a <code>conditional</code> tool type can set <code>branch_true</code> and <code>branch_false</code> to the <code>order</code> number of any other step. Execution jumps to that step instead of continuing sequentially. A <code>null</code> value means "continue to the next step." Execution stops after 100 iterations to prevent loops.</p>

              <p><strong>Response:</strong> Returns the final output, a context map of every step's output, and per-step results:</p>
              <pre><code>{`{
  "success": true,
  "output": { /* final step's output */ },
  "context": {
    "step_0": { /* step 1 output */ },
    "step_1": { /* step 2 output */ }
  },
  "results": [
    {
      "step": 0,
      "tool": "Tool Name",
      "result": { "success": true, "output": { ... } }
    }
    // ... one entry per executed step
  ]
}

// On failure:
{
  "success": false,
  "error": "Step 1 (Tool Name) failed: <reason>",
  "results": [ /* steps that ran before failure */ ]
}`}</code></pre>
            </div>

            <div className="endpoint">
              <h5>POST /api/v1/image/analyze/</h5>
              <p>Analyze images with vision models (requires llava or similar)</p>
              <pre><code>{`// Multipart form-data
{
  "image": <file>,          // Image file upload
  "prompt": "What is in this image?",
  "model": "llava",         // optional, default: llava
  "stream": false           // optional
}

// OR JSON with base64
{
  "image": "base64_encoded_image",
  "prompt": "Describe this image",
  "model": "llava"
}`}</code></pre>
              <p><strong>Setup:</strong> Install vision model on Ollama: <code>ollama pull llava</code></p>
            </div>

            <div className="endpoint">
              <h5>POST /api/v1/image/generate/</h5>
              <p>Generate images from text (requires additional setup)</p>
              <pre><code>{`{
  "prompt": "A beautiful sunset over mountains",
  "model": "stable-diffusion",  // optional
  "width": 512,                 // optional
  "height": 512,                // optional
  "steps": 20                   // optional
}`}</code></pre>
              <p><strong>Note:</strong> Requires Stable Diffusion or similar image generation service setup.</p>
            </div>

            <h4>Example (curl)</h4>
            <pre><code>{`curl -X POST http://localhost:8000/api/v1/summarize/ \\
  -H "Authorization: Bearer sk-your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{"text": "Your text here", "length": "short"}'`}</code></pre>

            <h4>Example (Python)</h4>
            <pre><code>{`import requests

API_KEY = "sk-your-api-key"
API_URL = "http://localhost:8000/api/v1/summarize/"

response = requests.post(
    API_URL,
    headers={"Authorization": f"Bearer {API_KEY}"},
    json={"text": "Your text here", "length": "short"}
)

print(response.json())`}</code></pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default APIKeyManager;
